'use client';

import { LoadingSpinner } from '../icons';
import { cn } from '../utils/cn';

/**
 * LoadingOverlay component that displays a full-screen loading overlay with spinner and message.
 *
 * Usage:
 * 1. For automatic navigation loading, use SimpleAppRouterProgressProvider in your app layout
 * 2. For manual loading states, use this component directly with isLoading prop
 *
 * @example
 * // Automatic navigation loading (recommended)
 * <SimpleAppRouterProgressProvider>
 *   {children}
 * </SimpleAppRouterProgressProvider>
 *
 * // Manual loading state
 * <LoadingOverlay isLoading={isLoading} message="Processing..." />
 */
interface LoadingOverlayProps {
  isLoading: boolean;
  message?: string;
  className?: string;
}

export default function LoadingOverlay({
  isLoading,
  message = 'Loading...',
  className,
}: LoadingOverlayProps) {
  if (!isLoading) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center',
        'bg-black/5 backdrop-blur-xs',
        'transition-opacity duration-200',
        className
      )}
    >
      <div className="flex flex-col items-center gap-4 rounded-lg bg-white p-6 shadow-lg">
        <LoadingSpinner className="h-8 w-8 text-el-blue-600" />
        <p className="text-sm font-medium text-el-grey-700">{message}</p>
      </div>
    </div>
  );
}
